import { axios } from '@api/axios.js';
import { createComment } from '@api/comments.js';
import { getModelByReconId } from '@api/models.js';
import {
  addCadFile,
  addModelManualToFolder,
  getPresignedUrl,
} from '@api/projects.js';
import ProjectsState from '@states/Projects.js';
import UserState from '@states/User.js';
import { getCadFileModelId, getReconstructionId } from '@utils/cadFileModel.js';
import { addProject } from '@utils/projectUtils.js';
import _socket from '@utils/socket.js';
import { S3_BUCKET } from '/environment.js';

import {
  automatedTagPosition,
  conditionResultsForItem,
  conditionSummaryText,
  exportScale,
  hasExportableSpatialVisionContent,
  tagText,
  tagTitle,
} from './automatedTagMapping.js';

const SCANNING_TYPE_IMAGE = 3;
const CONDITION_SUMMARY_TITLE = 'Spatial Vision Condition Summary';

const debugSource = source => ({
  taskId: source?.taskId || null,
  cadFileModelId: getCadFileModelId(source),
  reconstructionId: getReconstructionId(source),
  hasPath: Boolean(sourceImagePath(source)),
  source: source?.source || null,
});

const extensionFrom = (name, mimeType = '') => {
  const cleanName = String(name || '')
    .split('?')[0]
    .split('#')[0];
  const nameExtension = cleanName.includes('.')
    ? cleanName.split('.').pop()
    : '';
  if (nameExtension) return nameExtension.toLowerCase();
  const mimeExtension = String(mimeType || '')
    .split('/')
    .pop();
  return mimeExtension || 'png';
};

const imageFileName = item => {
  const sourceName = item?.filename || item?.originalName || item?.name;
  const base = sourceName ? String(sourceName) : 'spatial-vision-image';
  return base.includes('.') ? base : `${base}.png`;
};

const storageKeyFromUrl = fileUrl => {
  if (S3_BUCKET && fileUrl.includes(S3_BUCKET)) {
    return fileUrl.split(S3_BUCKET)[1];
  }
  try {
    return new URL(fileUrl).pathname;
  } catch {
    return fileUrl;
  }
};

const cacheBustedUrl = url => {
  const separator = String(url).includes('?') ? '&' : '?';
  return `${url}${separator}cors_bust=${Date.now()}`;
};

const normalizeSourcePath = value =>
  String(value || '')
    .split('?')[0]
    .split('#')[0];

const sourceImagePath = sourceImage =>
  sourceImage?.path ||
  sourceImage?.filePath ||
  sourceImage?.url ||
  sourceImage?.imageUrl ||
  null;

const projectOptions = () =>
  [...(ProjectsState.projects.folders || [])]
    .filter(project => project?.id)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

const taskOptions = projectId =>
  [...(ProjectsState.projects.tasks || [])]
    .filter(task => String(task.parentId) === String(projectId))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

const organizationIdForProjectType = projectType =>
  projectType === 'team' ? UserState.user?.selectedOrg || null : null;

export const automatedTagsProjectTypes = {
  PERSONAL: 'personal',
  TEAM: 'team',
};

export function getCurrentAutomatedTagsProjectType() {
  return (
    UserState.user?.selectedProjectsType || automatedTagsProjectTypes.PERSONAL
  );
}

export const automatedTagsDestinations = {
  NEW_PROJECT: 'new-project',
  NEW_TASK: 'new-task',
  EXISTING_TASK: 'existing-task',
};

export const automatedTagsDestinationLabels = {
  [automatedTagsDestinations.NEW_PROJECT]: 'New project and task',
  [automatedTagsDestinations.NEW_TASK]: 'New task in project',
  [automatedTagsDestinations.EXISTING_TASK]: 'Existing task',
};

export async function ensureProjectsForAutomatedTags(
  projectType = getCurrentAutomatedTagsProjectType(),
) {
  const organizationId = organizationIdForProjectType(projectType);
  const loadedOrganizationId =
    ProjectsState.projects.rootFolderOrganizationId || null;
  if (
    !ProjectsState.projects.hasBeenLoaded ||
    String(loadedOrganizationId || '') !== String(organizationId || '')
  ) {
    await ProjectsState.loadProjects(organizationId);
  }
  return ProjectsState.projects;
}

export async function ensureProjectTasks(projectId) {
  if (!projectId) return [];
  await ProjectsState.getProjectById(projectId);
  return taskOptions(projectId);
}

export function getAutomatedTagsProjectOptions() {
  return projectOptions();
}

export function getAutomatedTagsTaskOptions(projectId) {
  return taskOptions(projectId);
}

function exportableBatchItems(item, items) {
  const hasBatchItems = Array.isArray(items) && items.length;
  const candidates = hasBatchItems ? items : [item];
  return candidates.filter(candidate => {
    if (!candidate) return false;
    if (hasBatchItems && candidate.status !== 'completed') return false;
    return hasExportableSpatialVisionContent(candidate);
  });
}

async function createAutomatedTag({
  detection,
  index,
  scale,
  taskId,
  baseModel,
  context,
}) {
  const position = automatedTagPosition(detection, scale);
  if (!position) throw new Error('Detection has no valid position');

  const formData = new FormData();
  formData.append('tagType', 'COMMENT');
  formData.append('title', tagTitle(detection, index));
  formData.append('baseModel', baseModel);
  formData.append('position', JSON.stringify(position));
  formData.append('cadFileId', taskId);
  formData.append('text', tagText(detection, context));
  formData.append('isTask', 'false');
  formData.append('taskStatus', null);

  const response = await createComment(formData);
  const commentId = response?.response?.commentId;
  if (!commentId) throw new Error('Tag creation failed');
  _socket.emit('send_tag_updates', {
    commentId,
    cadFileId: Number(taskId),
  });
  return response.response;
}

async function createConditionSummaryComment({ item, taskId }) {
  const text = conditionSummaryText(item);
  if (!text) return null;

  const formData = new FormData();
  formData.append('title', CONDITION_SUMMARY_TITLE);
  formData.append('cadFileId', taskId);
  formData.append('text', text);
  formData.append('isTask', 'false');

  const response = await createComment(formData);
  const commentId = response?.response?.commentId;
  if (!commentId) throw new Error('Condition summary comment creation failed');
  _socket.emit('send_tag_updates', {
    commentId,
    cadFileId: Number(taskId),
  });
  return response.response;
}

export async function createAutomatedTagsForExistingImage({
  item,
  sourceImage,
  taskId = sourceImage?.taskId,
  baseModel = getCadFileModelId(sourceImage),
}) {
  const detections = item?.detections || [];
  const conditionResults = conditionResultsForItem(item);
  if (!hasExportableSpatialVisionContent(item)) {
    return {
      createdCount: 0,
      failedCount: 0,
      summaryCreated: false,
      summaryFailed: null,
      summaryComment: null,
      totalCount: 0,
      conditionResultCount: 0,
      createdTags: [],
      failedTags: [],
    };
  }
  const resolvedBaseModel =
    baseModel || (await resolveCadFileModelIdFromSource(sourceImage));

  console.info('[DEBUG-auto-tags] Existing image tag target resolved', {
    source: debugSource(sourceImage),
    providedBaseModel: baseModel || null,
    resolvedBaseModel: resolvedBaseModel || null,
    detectionCount: detections.length,
    conditionResultCount: conditionResults.length,
  });

  if (!taskId || !resolvedBaseModel) {
    throw new Error('Source task and file are required for automated tags');
  }

  const createdTags = [];
  const failedTags = [];
  const scale = exportScale(item);

  for (let index = 0; index < detections.length; index += 1) {
    const detection = detections[index];
    try {
      const tag = await createAutomatedTag({
        detection,
        index,
        scale,
        taskId,
        baseModel: resolvedBaseModel,
        context: item,
      });
      createdTags.push(tag);
    } catch (error) {
      failedTags.push({ detection, error });
    }
  }

  let summaryComment = null;
  let summaryFailed = null;
  if (conditionSummaryText(item)) {
    try {
      summaryComment = await createConditionSummaryComment({ item, taskId });
    } catch (error) {
      summaryFailed = error;
    }
  }

  return {
    createdCount: createdTags.length,
    failedCount: failedTags.length,
    summaryCreated: Boolean(summaryComment),
    summaryFailed,
    summaryComment,
    totalCount: detections.length,
    conditionResultCount: conditionResults.length,
    createdTags,
    failedTags,
  };
}

async function resolveCadFileModelIdFromSource(sourceImage) {
  const reconstruction = findTaskReconstructionForSource(sourceImage);
  const localModelId = getCadFileModelId(reconstruction);
  if (localModelId) return localModelId;

  const reconstructionId =
    getReconstructionId(sourceImage) || getReconstructionId(reconstruction);
  if (!reconstructionId) return null;

  const response = await getModelByReconId(reconstructionId);
  const modelId =
    response?.body?.cadFileModelId ||
    response?.body?.cadfilemodel?.cadFileModelId ||
    response?.body?.cadfilemodels?.[0]?.cadFileModelId ||
    response?.cadFileModelId ||
    response?.cadfilemodel?.cadFileModelId ||
    response?.cadfilemodels?.[0]?.cadFileModelId ||
    null;

  return modelId;
}

function findTaskReconstructionForSource(sourceImage) {
  const taskId = sourceImage?.taskId;
  const sourcePath = normalizeSourcePath(sourceImagePath(sourceImage));
  if (!taskId || !sourcePath) return null;

  const task = (ProjectsState.projects.tasks || []).find(
    item => String(item.id) === String(taskId),
  );
  if (!task?.reconstructions?.length) return null;

  const reconstruction =
    task.reconstructions.find(reconstruction => {
      const paths = [
        reconstruction?.filePath,
        reconstruction?.path,
        reconstruction?.url,
        reconstruction?.fileUrl,
        reconstruction?.thumbnail,
        reconstruction?.imageUrl,
      ].map(normalizeSourcePath);
      return paths.includes(sourcePath);
    }) || null;

  return reconstruction;
}

async function createTask(projectId, taskName) {
  const response = await addCadFile({
    name: taskName,
    modelFolderId: projectId,
    reconstructions: [],
  });
  if (response.status !== 'success' || !response.response?.cadFileID) {
    throw new Error('Failed to create task');
  }
  const task = {
    id: response.response.cadFileID,
    name: taskName || 'Untitled',
    createdAt: response.response.createdAt || new Date().toISOString(),
    updatedAt: response.response.updatedAt || new Date().toISOString(),
    thumbnail: null,
    parentId: projectId,
    sharingId: response.response.sharingId || null,
    uid: response.response.uid || null,
    organizationId: response.response.organizationId || null,
    cadfileAccessLevel: response.response.cadfileAccessLevel || null,
    commentsEnabled: response.response.commentsEnabled || false,
    reconstructions: [],
    rowOrder: response.response.rowOrder || null,
  };
  ProjectsState.setProjects({
    ...ProjectsState.projects,
    tasks: [...(ProjectsState.projects.tasks || []), task],
  });
  return task;
}

async function resolveDestination(destination) {
  if (destination.mode === automatedTagsDestinations.NEW_PROJECT) {
    const project = await addProject(destination.projectName, 'projects');
    const task = await createTask(project.id, destination.taskName);
    return { project, task };
  }

  if (destination.mode === automatedTagsDestinations.NEW_TASK) {
    const project = projectOptions().find(
      item => String(item.id) === String(destination.projectId),
    );
    const task = await createTask(destination.projectId, destination.taskName);
    return { project, task };
  }

  const task = (ProjectsState.projects.tasks || []).find(
    item => String(item.id) === String(destination.taskId),
  );
  if (!task) throw new Error('Select an existing task');
  const project = projectOptions().find(
    item => String(item.id) === String(task.parentId),
  );
  return { project, task };
}

async function fetchImageAsFile(item) {
  if (!item?.url) throw new Error('Spatial Vision image URL is missing');
  const response = await fetch(cacheBustedUrl(item.url), {
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`Failed to download image (${response.status})`);
  }
  const blob = await response.blob();
  const filename = imageFileName(item);
  const type = blob.type || 'image/png';
  return new File([blob], filename, { type });
}

async function uploadImageToTask(taskId, item) {
  const file = await fetchImageAsFile(item);
  const type = extensionFrom(file.name, file.type);
  const presignedUrlResponse = await getPresignedUrl(SCANNING_TYPE_IMAGE, type);
  if (presignedUrlResponse.status !== 200 || !presignedUrlResponse.body) {
    throw new Error('Failed to get upload URL');
  }

  const presignedUrl = presignedUrlResponse.body;
  const uploadResponse = await axios.put(presignedUrl, file, {
    headers: file.type ? { 'Content-Type': file.type } : {},
  });
  if (uploadResponse.status !== 200) {
    throw new Error('Failed to upload image');
  }

  const fileUrl = presignedUrl.split('?')[0];
  const payload = {
    files: JSON.stringify([
      {
        size: file.size,
        originalname: file.name,
        key: storageKeyFromUrl(fileUrl),
        location: fileUrl,
      },
    ]),
    uploadData: JSON.stringify([{ cadFileId: taskId, isPanorama: false }]),
  };
  const response = await addModelManualToFolder(SCANNING_TYPE_IMAGE, payload);
  const uploadedModel = response?.body?.cadfilemodels?.[0];
  if (response?.error || !uploadedModel?.cadFileModelId) {
    throw new Error('Failed to attach image to task');
  }
  return uploadedModel;
}

async function exportAutomatedTagsItem({ item, task }) {
  const uploadedModel = await uploadImageToTask(task.id, item);
  const result = await createAutomatedTagsForExistingImage({
    item,
    taskId: task.id,
    baseModel: uploadedModel.cadFileModelId,
  });
  return { item, uploadedModel, ...result };
}

export async function exportAutomatedTags({ item, items, destination }) {
  const selectedItems = exportableBatchItems(item, items);
  if (!selectedItems.length) {
    throw new Error('No detections or condition results to export');
  }

  const { project, task } = await resolveDestination(destination);
  const imageResults = [];
  for (const exportItem of selectedItems) {
    try {
      const result = await exportAutomatedTagsItem({
        item: exportItem,
        task,
      });
      imageResults.push({
        item: exportItem,
        status: 'success',
        ...result,
      });
    } catch (error) {
      imageResults.push({
        item: exportItem,
        status: 'failed',
        error,
        createdCount: 0,
        failedCount: 0,
        summaryCreated: false,
        summaryFailed: null,
        totalCount: exportItem?.detections?.length || 0,
        conditionResultCount: conditionResultsForItem(exportItem).length,
        createdTags: [],
        failedTags: [],
      });
    }
  }

  const createdCount = imageResults.reduce(
    (sum, result) => sum + (result.createdCount || 0),
    0,
  );
  const tagFailureCount = imageResults.reduce(
    (sum, result) => sum + (result.failedTags?.length || 0),
    0,
  );
  const imageFailedCount = imageResults.filter(
    result => result.status === 'failed',
  ).length;
  const summaryFailedCount = imageResults.filter(
    result => result.summaryFailed,
  ).length;
  const totalCount = imageResults.reduce(
    (sum, result) => sum + (result.totalCount || 0),
    0,
  );
  const conditionResultCount = selectedItems.reduce(
    (sum, exportItem) => sum + conditionResultsForItem(exportItem).length,
    0,
  );
  const summaryCreatedCount = imageResults.filter(
    result => result.summaryCreated,
  ).length;

  return {
    project,
    task,
    uploadedModel:
      imageResults.find(result => result.uploadedModel)?.uploadedModel || null,
    imageCount: selectedItems.length,
    exportedImageCount: imageResults.filter(
      result => result.status === 'success',
    ).length,
    createdCount,
    tagFailedCount: tagFailureCount,
    imageFailedCount,
    failedCount: tagFailureCount + imageFailedCount + summaryFailedCount,
    totalCount,
    conditionResultCount,
    summaryCreated: summaryCreatedCount > 0,
    summaryCreatedCount,
    summaryFailedCount,
    imageResults,
    viewerUrl: task.sharingId ? `/viewer?sharingId=${task.sharingId}` : null,
  };
}
